import { ReactNode, useId, useRef, useState } from 'react';
import { Combobox, Input, useCombobox } from '@mantine/core';
import { Calendar } from '@mantine/dates';
import { useUncontrolled } from '@mantine/hooks';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { DateTime } from 'luxon';

import { FormLabel } from '../formlabel/FormLabel';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';

import { useIsDayExcluded } from './helper/useIsDayExcluded';
import { DateInput } from './DateInput';

import calendarStyles from './calendar.module.scss';
import styles from './date-picker.module.scss';

export type SingleDatePickerProps = {
  availableDates?: Date[];
  dateTimeFormatOptions?: Intl.DateTimeFormatOptions;
  defaultValue?: Date | null;
  disabled?: boolean;
  readOnly?: boolean;
  clearable?: boolean;
  icon?: string;
  label?: ReactNode | ReactNode[];
  loading?: boolean;
  onChange: (newValue: Date | null) => void;
  placeholder: string;
  value?: Date | null;
  withinPortal?: boolean;
  minDate?: DateTime;
  maxDate?: DateTime;
};

export function SingleDatePicker({
  availableDates,
  clearable = true,
  dateTimeFormatOptions,
  defaultValue,
  disabled,
  readOnly = false,
  icon = 'event',
  label,
  onChange,
  loading,
  placeholder,
  value,
  withinPortal = true,
  minDate,
  maxDate,
}: SingleDatePickerProps) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isDayExcluded = useIsDayExcluded(availableDates);
  // select first available date per default or fallback to today
  const [currentDate, setCurrentDate] = useState(
    availableDates && availableDates.length > 0 ? availableDates[0] : new Date()
  );
  // note, we use null here so we can differentiate between empty controlled or unset variable
  const [selection, setSelection] = useUncontrolled<Date | null>({
    value,
    defaultValue,
    onChange,
  });
  const combobox = useCombobox({
    onDropdownClose: () => {
      inputRef.current?.blur();
    },
  });

  return (
    <Combobox
      data-testid="date-picker"
      disabled={disabled || loading || readOnly}
      store={combobox}
      withinPortal={withinPortal}
      classNames={{
        dropdown: styles['dropdown'],
        options: styles['options'],
        header: styles['dropdown-header'],
      }}
    >
      <Combobox.EventsTarget>
        <Input.Wrapper
          id={id}
          classNames={{
            label: clsx(styles['label'], {
              [styles['label--disabled']]: disabled,
            }),
          }}
          onBlur={() => combobox.closeDropdown()}
        >
          <FormLabel
            id={id}
            disabled={disabled}
            loading={loading}
            label={label}
          />
          <Combobox.DropdownTarget>
            <DateInput
              ref={inputRef}
              clearable={clearable}
              data-testid="date-picker"
              loading={loading}
              disabled={disabled === true}
              readOnly={readOnly}
              icon={icon}
              focused={combobox.dropdownOpened}
              value={selection}
              placeholder={placeholder}
              dateTimeFormatOptions={dateTimeFormatOptions}
              onChange={(date) => {
                if (
                  (minDate && date.getTime() < minDate.toMillis()) ||
                  (maxDate && date.getTime() > maxDate.toMillis())
                ) {
                  return;
                }
                setCurrentDate(date);

                if (!isDayExcluded(date)) {
                  setSelection(date);
                }
              }}
              onClear={() => setSelection(null)}
              onFocus={() => {
                if (selection) {
                  setCurrentDate(selection);
                }
                combobox.openDropdown();
              }}
            />
          </Combobox.DropdownTarget>
        </Input.Wrapper>
      </Combobox.EventsTarget>
      <Combobox.Dropdown>
        <Combobox.Options>
          <GoogleFontIcon
            name="arrow_drop_up"
            solid
            data-position="start"
            className={styles['dropdown-arrow']}
          />
          <Calendar
            locale={navigator.language}
            date={currentDate}
            onDateChange={setCurrentDate}
            excludeDate={isDayExcluded}
            minDate={minDate?.toJSDate()}
            maxDate={maxDate?.toJSDate()}
            classNames={{
              calendarHeaderControl: calendarStyles['headerControl'],
              calendarHeaderLevel: calendarStyles['headerLevel'],
              day: calendarStyles['day'],
              monthsListCell: calendarStyles['month'],
              yearsListCell: calendarStyles['year'],
            }}
            getDayProps={(_date) => {
              // ensure our date has the correct time format
              const date = new Date(`${dayjs(_date).format('YYYY-MM-DD')}`);
              const selected = selection
                ? dayjs(date).isSame(selection, 'date')
                : false;
              return {
                selected,
                onClick: (e) => {
                  setSelection(date);
                  combobox.closeDropdown();
                },
              };
            }}
          />
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
